import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * AchatDetailTable
 * Tableau de saisie détaillée des achats par fournisseur.
 * Props:
 *   - chargeType : string (ex: "Achat Spintoss")
 *   - onTotalChange : function(total) → appelée à chaque mise à jour du total
 *   - lignes / setLignes : state géré depuis le parent pour récupérer les lignes
 */
const AchatDetailTable = ({ chargeType, onTotalChange, lignes, setLignes }) => {
  const [suppliers, setSuppliers] = useState([]);
  const [showNewSupplierForm, setShowNewSupplierForm] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [addingSupplierRowIdx, setAddingSupplierRowIdx] = useState(null);
  const [savingSupplier, setSavingSupplier] = useState(false);

  // Charger la liste des fournisseurs
  useEffect(() => {
    axios.get('/api/suppliers/getSuppliers')
      .then(res => setSuppliers(res.data.suppliers || []))
      .catch(err => console.error('Erreur chargement fournisseurs:', err));
  }, []);

  // Recalculer le total à chaque changement de lignes
  useEffect(() => {
    const total = lignes.reduce((sum, l) => sum + (parseFloat(l.montant) || 0), 0);
    onTotalChange(total);
  }, [lignes, onTotalChange]);

  // Ajouter une ligne vide (date par défaut = aujourd'hui)
  const addLigne = () => {
    const today = new Date().toISOString().split('T')[0];
    setLignes(prev => [...prev, { fournisseur: '', dateAchat: today, montant: 0 }]);
  };

  // Supprimer une ligne
  const removeLigne = (idx) => {
    setLignes(prev => prev.filter((_, i) => i !== idx));
  };

  // Mettre à jour un champ d'une ligne
  const updateLigne = (idx, field, value) => {
    setLignes(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      // Mettre à jour le montant directement
      if (field === 'montant') {
        updated[idx].montant = parseFloat(value) || 0;
      }
      return updated;
    });
  };

  // Créer un nouveau fournisseur directement
  const handleAddNewSupplier = async () => {
    if (!newSupplierName.trim()) return;
    setSavingSupplier(true);
    try {
      const res = await axios.post('/api/suppliers/addSupplier', { companyName: newSupplierName.trim() });
      const newSupplier = res.data.supplier;
      setSuppliers(prev => [...prev, newSupplier]);
      // Affecter le nouveau fournisseur à la ligne concernée
      if (addingSupplierRowIdx !== null) {
        updateLigne(addingSupplierRowIdx, 'fournisseur', newSupplier.companyName);
      }
      setNewSupplierName('');
      setShowNewSupplierForm(false);
      setAddingSupplierRowIdx(null);
    } catch (err) {
      console.error('Erreur création fournisseur:', err);
      alert('Erreur lors de la création du fournisseur.');
    } finally {
      setSavingSupplier(false);
    }
  };

  const total = lignes.reduce((sum, l) => sum + (parseFloat(l.montant) || 0), 0);

  return (
    <div style={styles.container}>
      {/* En-tête */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.badge}>🛒 {chargeType}</span>
          <span style={styles.subtitle}>Détail des achats par fournisseur</span>
        </div>
        <button type="button" style={styles.addBtn} onClick={addLigne}>
          + Ajouter une ligne
        </button>
      </div>

      {/* Tableau */}
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Fournisseur</th>
              <th style={{ ...styles.th, width: '150px' }}>Date d'achat</th>
              <th style={{ ...styles.th, width: '140px', textAlign: 'right' }}>Montant (TND)</th>
              <th style={{ ...styles.th, width: '50px' }}></th>
            </tr>
          </thead>
          <tbody>
            {lignes.length === 0 ? (
              <tr>
                <td colSpan={4} style={styles.emptyRow}>
                  Aucune ligne — cliquez sur <strong>+ Ajouter une ligne</strong> pour commencer
                </td>
              </tr>
            ) : (
              lignes.map((ligne, idx) => (
                <tr key={idx} style={styles.tr}>
                  {/* Fournisseur */}
                  <td style={styles.td}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <select
                        value={ligne.fournisseur}
                        onChange={e => {
                          if (e.target.value === '__new__') {
                            setAddingSupplierRowIdx(idx);
                            setShowNewSupplierForm(true);
                          } else {
                            updateLigne(idx, 'fournisseur', e.target.value);
                          }
                        }}
                        style={styles.select}
                        required
                      >
                        <option value="">-- Choisir --</option>
                        {suppliers.map(s => (
                          <option key={s._id} value={s.companyName}>{s.companyName}</option>
                        ))}
                        <option value="__new__">➕ Nouveau fournisseur...</option>
                      </select>
                    </div>
                  </td>

                  {/* Date d'achat */}
                  <td style={{ ...styles.td, width: '150px' }}>
                    <input
                      type="date"
                      value={ligne.dateAchat || new Date().toISOString().split('T')[0]}
                      onChange={e => updateLigne(idx, 'dateAchat', e.target.value)}
                      style={{ ...styles.input, fontSize: '0.85rem' }}
                    />
                  </td>

                  {/* Montant direct */}
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <input
                      type="number"
                      value={ligne.montant === 0 ? '' : ligne.montant}
                      onChange={e => updateLigne(idx, 'montant', e.target.value)}
                      min="0"
                      step="0.001"
                      placeholder="0.000"
                      style={{ ...styles.input, textAlign: 'right', fontWeight: '700', color: '#059669' }}
                      required
                    />
                  </td>

                  {/* Supprimer */}
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => removeLigne(idx)}
                      style={styles.deleteBtn}
                      title="Supprimer cette ligne"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal création fournisseur */}
      {showNewSupplierForm && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h4 style={styles.modalTitle}>➕ Nouveau Fournisseur</h4>
            <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '16px' }}>
              Saisissez le nom du fournisseur. Vous pourrez compléter ses informations plus tard.
            </p>
            <input
              type="text"
              value={newSupplierName}
              onChange={e => setNewSupplierName(e.target.value)}
              placeholder="Ex: Café Express Sarl"
              style={styles.modalInput}
              autoFocus
              onKeyDown={e => e.key === 'Enter' && handleAddNewSupplier()}
            />
            <div style={styles.modalActions}>
              <button
                type="button"
                onClick={handleAddNewSupplier}
                disabled={savingSupplier || !newSupplierName.trim()}
                style={styles.modalConfirmBtn}
              >
                {savingSupplier ? 'Enregistrement...' : 'Créer & Sélectionner'}
              </button>
              <button
                type="button"
                onClick={() => { setShowNewSupplierForm(false); setNewSupplierName(''); setAddingSupplierRowIdx(null); }}
                style={styles.modalCancelBtn}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ligne Total */}
      {lignes.length > 0 && (
        <div style={styles.totalRow}>
          <div style={styles.totalLabel}>
            <span>🧾 Total {chargeType}</span>
            <span style={styles.lignesCount}>{lignes.length} ligne{lignes.length > 1 ? 's' : ''}</span>
          </div>
          <div style={styles.totalValue}>
            {total.toFixed(3)} <span style={styles.currency}>TND</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  container: {
    background: 'linear-gradient(135deg, rgba(59,130,246,0.04) 0%, rgba(147,51,234,0.04) 100%)',
    border: '1.5px solid rgba(59,130,246,0.2)',
    borderRadius: '16px',
    padding: '20px',
    margin: '12px 0 20px 0',
    boxShadow: '0 4px 20px rgba(59,130,246,0.08)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
  },
  badge: {
    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    color: '#fff',
    padding: '5px 14px',
    borderRadius: '20px',
    fontWeight: '700',
    fontSize: '0.85rem',
    letterSpacing: '0.5px',
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '0.9rem',
    fontStyle: 'italic',
  },
  addBtn: {
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '8px 18px',
    fontWeight: '700',
    fontSize: '0.9rem',
    cursor: 'pointer',
    transition: 'transform 0.15s, box-shadow 0.15s',
    boxShadow: '0 2px 8px rgba(16,185,129,0.3)',
  },
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: '10px',
    border: '1px solid rgba(59,130,246,0.15)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.92rem',
  },
  th: {
    background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    color: '#fff',
    padding: '11px 12px',
    fontWeight: '700',
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  tr: {
    transition: 'background 0.15s',
    background: '#fff',
  },
  td: {
    padding: '8px 10px',
    borderBottom: '1px solid rgba(59,130,246,0.1)',
    verticalAlign: 'middle',
  },
  input: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.9rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '7px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '0.9rem',
    outline: 'none',
    cursor: 'pointer',
    background: '#fff',
  },
  montantBadge: {
    fontWeight: '700',
    color: '#059669',
    fontSize: '0.95rem',
  },
  deleteBtn: {
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    color: '#ef4444',
    borderRadius: '6px',
    width: '28px',
    height: '28px',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '0.8rem',
  },
  emptyRow: {
    textAlign: 'center',
    padding: '28px',
    color: '#9ca3af',
    fontSize: '0.9rem',
    fontStyle: 'italic',
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    background: 'linear-gradient(135deg, #059669, #10b981)',
    borderRadius: '12px',
    padding: '14px 20px',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
  },
  totalLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontWeight: '700',
    fontSize: '1rem',
  },
  lignesCount: {
    background: 'rgba(255,255,255,0.2)',
    borderRadius: '10px',
    padding: '2px 10px',
    fontSize: '0.8rem',
  },
  totalValue: {
    fontWeight: '800',
    fontSize: '1.4rem',
  },
  currency: {
    fontSize: '0.9rem',
    fontWeight: '600',
    opacity: 0.85,
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modal: {
    background: '#fff',
    borderRadius: '18px',
    padding: '32px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    marginBottom: '8px',
    color: '#1e293b',
    fontWeight: '700',
    fontSize: '1.2rem',
  },
  modalInput: {
    width: '100%',
    padding: '12px 14px',
    border: '1.5px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '1rem',
    outline: 'none',
    marginBottom: '16px',
    boxSizing: 'border-box',
  },
  modalActions: {
    display: 'flex',
    gap: '10px',
  },
  modalConfirmBtn: {
    flex: 1,
    background: 'linear-gradient(135deg, #10b981, #059669)',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '12px',
    fontWeight: '700',
    fontSize: '0.95rem',
    cursor: 'pointer',
  },
  modalCancelBtn: {
    flex: 1,
    background: '#f1f5f9',
    color: '#64748b',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    padding: '12px',
    fontWeight: '600',
    fontSize: '0.95rem',
    cursor: 'pointer',
  },
};

export default AchatDetailTable;
